import { cloneDeep, isEqual } from "lodash"
import { Player } from "@/types/player"
import { Collapse } from "@mui/material"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import classNames from "classnames"

import BasicButton from "@/components/button/BasicButton"
import Dialog from "@/components/dialog/Dialog"
import Icon from "@/components/icon/Icon"
import TextField from "@/components/form/textField/TextField"
import Radio from "@/components/form/radio/Radio"
import { colors } from "@/components/colors"
import { colorTokens } from "@/components/colorTokens"
import {
  ManageDeviceDialogProps,
  DeviceMathDialogTableProps,
} from "@/app/settings/club/players/type"
import {
  useGetSelectedLabel,
  useSortDevice,
  useSortPlayer,
  useGetSearchResult,
} from "@/app/settings/club/players/hooks/manageDeviceDialog"
import { useAssignablePlayersQuery } from "@/hooks/api/usePlayerQuery"
import {
  useAssignableDevicesQuery,
  useMatchedDeviceQuery,
  useUpsertMatchPlayerAndDeviceMutation,
} from "@/hooks/api/useDeviceQuery"
import { Device } from "@/types/device"
import { createGlobalStore } from "@/stores/global"

import "./manageDeviceDialog.scss"

export default function ManageDeviceDialog({ open, onClose }: ManageDeviceDialogProps) {
  const { t } = useTranslation()

  const [selectedPlayer, setSelectedPlayer] = useState<Player>()
  const [selectedDevice, setSelectedDevice] = useState<Device>()
  const [isPlayerOpen, setIsPlayerOpen] = useState(true)
  const [isDeviceOpen, setIsDeviceOpen] = useState(!!selectedPlayer)
  const [assignablePlayer, setAssignablePlayer] = useState<Player[] | undefined>()
  const [assignableDevice, setAssignableDevice] = useState<Device[] | undefined>()
  const [matchedDeviceList, setMatchedDevice] = useState<Device[] | undefined>()

  const { data: players } = useAssignablePlayersQuery()
  const { data: devices } = useAssignableDevicesQuery(selectedPlayer?.idfPlayer as number)
  const { data: matched } = useMatchedDeviceQuery()

  const { mutateAsync: mutateMatched, isPending } = useUpsertMatchPlayerAndDeviceMutation()
  const { setSnackBar } = createGlobalStore((state) => ({ setSnackBar: state.setSnackBar }))

  const togglePlayerOpen = () => {
    setIsPlayerOpen(!isPlayerOpen)
  }

  const toggleOpen = () => {
    if (!selectedPlayer) {
      return
    }

    setIsDeviceOpen(!isDeviceOpen)
    setIsPlayerOpen(!isPlayerOpen)
  }

  const unMatchDevice = (device: Device) => {
    let idx = -1

    if (matchedDeviceList) {
      idx = matchedDeviceList.findIndex((d) => d.idfPlayer === device.idfPlayer)
    }

    if (idx > -1) {
      const { idfPlayer, namePlayer, numberPlayer, number, version, model } =
        matchedDeviceList?.[idx] || {}
      matchedDeviceList?.splice(idx, 1)

      device.serialNumber = `${model}-${version}-${number}`

      setMatchedDevice([...(matchedDeviceList as Device[])])
      if (assignablePlayer) {
        setAssignablePlayer(
          [
            ...assignablePlayer,
            { idfPlayer, nickname: namePlayer, uniformNumber: numberPlayer } as Player,
          ].sort(useSortPlayer),
        )
      }
      if (assignableDevice) {
        setAssignableDevice(
          [
            ...assignableDevice,
            {
              idfPlayer,
              model,
              version,
              number,
              serialNumber: `${model}-${version}-${number}`,
            } as Device,
          ].sort(useSortDevice),
        )
      }
    }
  }

  const matchDevice = () => {
    if (selectedPlayer && selectedDevice) {
      const { idfPlayer, nickname, uniformNumber } = selectedPlayer
      const { model, version, number } = selectedDevice

      const device: Device = {
        idfPlayer,
        namePlayer: nickname,
        numberPlayer: uniformNumber,
        model,
        version,
        number,
        serialNumber: `${model}-${version}-${number}`,
      }

      if (matchedDeviceList) {
        setMatchedDevice([...matchedDeviceList, device].sort(useSortDevice))
      }

      setAssignablePlayer(assignablePlayer?.filter((player) => player.idfPlayer !== idfPlayer))

      setSelectedDevice(undefined)
      setSelectedPlayer(undefined)
      setIsDeviceOpen(false)

      setAssignableDevice(
        assignableDevice?.filter((device) => device.serialNumber !== selectedDevice.serialNumber),
      )

      setIsPlayerOpen(true)
    }
  }

  const cleanUp = () => {
    setSelectedDevice(undefined)
    setSelectedPlayer(undefined)
    setIsDeviceOpen(false)
    setIsPlayerOpen(true)

    setAssignablePlayer(cloneDeep(players)?.sort(useSortPlayer))
    setAssignableDevice(cloneDeep(devices)?.sort(useSortDevice))
    setMatchedDevice(cloneDeep(matched))
  }

  const uploadMatchedDevices = async () => {
    if (!matchedDeviceList) {
      return
    }

    const payload = matchedDeviceList.map((device) => {
      const { idfPlayer, model, number, version } = device
      const serialNumber = `${model}-${version}-${number.toString().padStart(5, "0")}`

      return { idfPlayer, model, number, version, serialNumber }
    })

    try {
      await mutateMatched(payload)

      setSnackBar({ message: t("messages.successUpdate"), open: true, severity: "success" })

      onClose()
      cleanUp()
    } catch (e: unknown) {
      return e
    }
  }

  useEffect(() => {
    setAssignablePlayer(cloneDeep(players)?.sort(useSortPlayer))
  }, [players])

  useEffect(() => {
    const fromServer = cloneDeep(devices) || []
    const localAdded =
      assignableDevice?.filter((device) =>
        fromServer?.every((d) => d.serialNumber !== device.serialNumber),
      ) || []

    // matchedDeviceList에 있는 기기들은 제외
    const available = [...fromServer, localAdded]
      .flat()
      .filter(
        (device) =>
          !matchedDeviceList?.some(
            (matched) =>
              `${matched.model}-${matched.version}-${matched.number}` === device.serialNumber,
          ),
      )

    setAssignableDevice(available.sort(useSortDevice))
  }, [devices, matchedDeviceList])

  useEffect(() => {
    setMatchedDevice(cloneDeep(matched))
  }, [matched])

  return (
    <Dialog
      className={classNames("ManageDeviceDialogCont")}
      open={open}
      onClose={() => {
        onClose()
        cleanUp()
      }}
      title={t("manageDevices")}
      maxWidth="900px"
      footer={
        <>
          <BasicButton
            type="default-outlined"
            size="md"
            onClick={() => {
              onClose()
              cleanUp()
            }}>
            <p>{t("button.cancel")}</p>
          </BasicButton>
          <BasicButton
            type="primary-contained"
            size="md"
            disabled={isEqual(matched, matchedDeviceList) || isPending}
            onClick={uploadMatchedDevices}>
            {t("button.save")}
          </BasicButton>
        </>
      }>
      <div className="manage-device-dialog-body">
        <div className="select-area">
          <div className="select-box">
            <DeviceMatchDialogTable
              title={t("selectPlayer")}
              items={assignablePlayer}
              selectable
              onChange={(value) => {
                setSelectedPlayer(
                  assignablePlayer?.find(
                    (player) => player.idfPlayer === (value as Player).idfPlayer,
                  ),
                )
                setIsDeviceOpen(true)
                togglePlayerOpen()
              }}
              labelKey="nickname"
              valueKey="idfPlayer"
              isOpen={isPlayerOpen}
              setIsOpen={toggleOpen}
              selected={selectedPlayer}
            />
          </div>
          <div className="select-box">
            <DeviceMatchDialogTable
              title={t("selectDevice")}
              items={assignableDevice}
              selectable
              onChange={(value) => {
                setSelectedDevice(
                  assignableDevice?.find(
                    (device) => device.serialNumber?.toString() === (value as Device).serialNumber,
                  ),
                )
              }}
              labelKey="serialNumber"
              valueKey="serialNumber"
              isOpen={isDeviceOpen}
              setIsOpen={toggleOpen}
              selected={selectedDevice}
            />
          </div>
          <BasicButton
            className="match-btn"
            disabled={!selectedDevice || !selectedPlayer}
            onClick={matchDevice}
            type="primary-contained"
            size="lg">
            <>
              <span>
                {t("button.match")}
                <Icon type="arrow-right" width={16} height={16} />
              </span>
            </>
          </BasicButton>
        </div>
        <div className="switch-ico">
          <Icon type="switch" />
        </div>
        <div className="select-area">
          <div className="select-box">
            <DeviceMatchDialogTable
              title={t("matchedPairs")}
              items={matchedDeviceList}
              selectable={false}
              onChange={(value) => unMatchDevice(value as Device)}
              labelKey="nickname"
              valueKey="idfPlayer"
              isOpen
            />
          </div>
        </div>
      </div>
    </Dialog>
  )
}

const DeviceMatchDialogTable = <T,>({
  title,
  items,
  selectable,
  onChange,
  isOpen,
  setIsOpen,
  selected,
}: DeviceMathDialogTableProps<T>) => {
  const [filteredItems, setFilteredItems] = useState<T[] | undefined>(items)
  const [search, setSearch] = useState("")
  const { t } = useTranslation()
  const label = useGetSelectedLabel(selected as Record<string, string>)
  const searchResult = useGetSearchResult(search, items as Array<Device | Player>)

  useEffect(() => {
    if (search.length < 2) {
      setFilteredItems(items)

      return
    }

    setFilteredItems(searchResult as T[])
  }, [items, search])

  const renderItems = () => {
    if (selectable) {
      return filteredItems?.map((item, index) => (
        <Radio
          className="select-box__cont__radio-group"
          key={index}
          label={`${(item as Player).uniformNumber || (item as Device).serialNumber || 0} ${(item as Player).nickname || ""}`}
          onChange={() => onChange(item as Player | Device)}
          checked={
            (!!(selected as Player)?.idfPlayer &&
              (selected as Player)?.idfPlayer === (item as Player).idfPlayer) ||
            (!!(selected as Device)?.serialNumber &&
              (selected as Device)?.serialNumber === (item as Device).serialNumber)
          }
        />
      ))
    }

    return filteredItems?.map((item, index) => (
      <div key={index} className="select-box__cont__item" onClick={() => onChange(item)}>
        <p>
          {(item as Device).numberPlayer || 0} {(item as Device).namePlayer}
        </p>
        <div>
          <p>
            {(item as Device).model}-{(item as Device).version}-
            {(item as Device).number.toString().padStart(5, "0")}
          </p>
          <p>
            <Icon type="close" width={20} height={20} />
          </p>
        </div>
      </div>
    ))
  }

  return (
    <div className={classNames("select-box__cont", { focus: isOpen && selectable })}>
      <div className="select-box__header" onClick={setIsOpen}>
        <p className="select-box__header__title">{title}</p>
        {selectable && (
          <div className="select-box__header__icon">
            {selected && <p className="selected-item">{label}</p>}
            <Icon type={isOpen ? "arrow-up" : "arrow-down"} color={colorTokens.field["ico-base"]} />
          </div>
        )}

        {!selectable && <p>{filteredItems?.length}</p>}
      </div>

      {selectable && (
        <Collapse
          className={classNames("select-box-content", { open: isOpen })}
          in={isOpen && !!filteredItems}
          timeout={{ exit: 300, enter: 300 }}>
          <TextField
            className="select-box__cont__search"
            placeholder={t("search")}
            value={search}
            fullWidth
            onChange={(e) => setSearch(e.target.value)}
            appendIcon={
              <Icon type="search" width={24} height={24} color={colors.neutral.grey[300]} />
            }
          />
          <div className="select-box__cont__body">{renderItems()}</div>
        </Collapse>
      )}

      {!selectable && (
        <div className="select-box-content none-selectable-content">
          <TextField
            className="select-box__cont__search"
            placeholder={t("search")}
            value={search}
            fullWidth
            onChange={(e) => setSearch(e.target.value)}
            appendIcon={
              <Icon type="search" width={24} height={24} color={colors.neutral.grey[300]} />
            }
          />
          <div className="select-box__cont__body">{renderItems()}</div>
        </div>
      )}
    </div>
  )
}
